import { useTranslation } from "react-i18next";

import type { AppCategory } from "@/api/queries";
import { categories } from "@/lib/categories";
import { formatDuration, percentOf } from "@/lib/format";
import { cn } from "@/lib/utils";

const fill: Record<AppCategory, string> = {
  productive: "bg-success-solid",
  neutral: "bg-muted/45",
  unproductive: "bg-danger-solid",
};

const text: Record<AppCategory, string> = {
  productive: "text-success",
  neutral: "text-muted",
  unproductive: "text-danger",
};

/** Colored dot with the category name for screen readers. */
export function CategoryDot({ category, className }: { category: AppCategory; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      role="img"
      aria-label={t(`categories.${category}`)}
      title={t(`categories.${category}`)}
      className={cn("inline-block size-2 shrink-0 rounded-full", fill[category], className)}
    />
  );
}

/** Category name with its dot. */
export function CategoryLabel({ category, className }: { category: AppCategory; className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", text[category], className)}>
      <span className={cn("size-2 rounded-full", fill[category])} aria-hidden="true" />
      {t(`categories.${category}`)}
    </span>
  );
}

type Totals = Record<AppCategory, number>;

/** Stacked bar of productive, neutral and unproductive time with a labeled legend. */
export function CategoryBar({
  totals,
  lang,
  className,
}: {
  totals: Totals;
  lang: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const sum = categories.reduce((s, c) => s + totals[c], 0);
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div
        className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-surface-muted"
        role="img"
        aria-label={categories
          .map((c) => `${t(`categories.${c}`)}: ${percentOf(totals[c], sum)}%`)
          .join(", ")}
      >
        {sum > 0 &&
          categories
            .filter((c) => totals[c] > 0)
            .map((c) => <div key={c} className={fill[c]} style={{ width: `${(totals[c] / sum) * 100}%` }} />)}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
        {categories.map((c) => (
          <span key={c} className="flex items-center gap-1.5">
            <span className={cn("size-2 rounded-full", fill[c])} aria-hidden="true" />
            {t(`categories.${c}`)}
            <span className="font-medium text-fg tabular-nums">{percentOf(totals[c], sum)}%</span>
            <span className="tabular-nums">· {formatDuration(totals[c], lang)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
